import React from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';

import {
  Box,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Chip,
  Icon,
  Link,
  Tooltip,
  Typography
} from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';

import { getFormattedValue } from '_core/utils/formatPrice';
import { getVisitorPhotoSrc } from '_core/utils/getVisitorPhotoSrc';
import { withLang } from '_core/hocs/withLang';
import { useConfig } from '_core/contexts/Config';
import { PriceMix } from '_core/components/PriceMix';

import { useStyle } from './style';

const RawGoodsListItem = (props) => {
  const { baseUrl, cart, data, getLangString: l } = props;

  const classes = useStyle();

  const config = useConfig();
  const shopCurrencyId = cart.shopCurrency?.id || cart.currency.id;

  const cartControl = cart.getControl(data);

  const info = cartControl.info;

  const groupPrice = info.group?.length ? info.group[0].price : null;
  const groupDiscount = info.group?.length ? info.group[0].discount : null;
  const groupPoints = info.group?.length ? info.group[0].points : null;

  const description = l(info.description);

  const link =
    baseUrl +
    '/' +
    (info.category ? info.category.pathString + '/' : '') +
    info.name;

  return (
    <Card key={data.id} className={classes.card}>
      <Box className={classes.mainRow}>
        <Link component={RouterLink} to={link}>
          {info.photo ? (
            <CardMedia
              className={classes.cardMedia}
              image={getVisitorPhotoSrc(info.photo, 'shop')}
            />
          ) : (
            ''
          )}
        </Link>

        <CardContent className={classes.cardContent}>
          <Box
            height="100%"
            display="flex"
            justifyContent="space-between"
            flexDirection="column"
          >
            <Typography
              variant="body2"
              color="textPrimary"
              className={classes.headerTitle}
            >
              {info.isLoading ? (
                <Skeleton
                  animation="wave"
                  variant="rect"
                  className={classes.skeleton}
                />
              ) : (
                <Link component={RouterLink} to={link}>
                  {l(info.title)}
                </Link>
              )}
            </Typography>

            {description ? (
              <Typography
                variant="caption"
                color="textPrimary"
                className={classes.headerDesciption}
              >
                {l(description)}
              </Typography>
            ) : null}
          </Box>
        </CardContent>

        <CardActions className={classes.cardActions}>
          {cartControl.count ? (
            <Icon
              className={classes.clearIcon}
              onClick={() => cartControl.remove()}
            >
              cancel
            </Icon>
          ) : (
            <Tooltip title={l('r.core.good.disabledelete')}>
              <Icon className={classes.clearIconDisable}>cancel</Icon>
            </Tooltip>
          )}
        </CardActions>
      </Box>

      <Box className={classes.priceRow} align="right">
        <Typography variant="body2" className={classes.priceText}>
          {cartControl.countTotal} {l('r.core.good.pieces')}
        </Typography>

        {info.group?.length > 1 ? (
          info.group.map((item, index) => (
            <PriceMix
              key={index}
              item={{
                currency: shopCurrencyId,
                value: item.price - item.discount
              }}
              itemProps={{
                className: classes.priceText,
                rootProps: {
                  color: 'secondary',
                  style: index > 0 ? { fontSize: '1rem' } : null
                },
                postfix: ` (${item.count} ${l('r.core.good.pieces')})`
              }}
            />
          ))
        ) : (
          <PriceMix
            item={{
              currency: shopCurrencyId,
              value: groupPrice - groupDiscount
            }}
            itemProps={{
              className: classes.priceText,
              rootProps: { color: 'secondary' }
            }}
          />
        )}

        {groupDiscount > 0 ? (
          <PriceMix
            item={{
              currency: shopCurrencyId,
              value: groupPrice
            }}
            className={classes.lineThrough + ' ' + classes.sale}
            itemProps={{
              className: classes.priceText,
              rootProps: { variant: 'caption' }
            }}
          />
        ) : null}
      </Box>

      {groupPoints > 0 ? (
        <Box
          display="flex"
          justifyContent="flex-end"
          className={classes.points}
        >
          <Chip
            size="small"
            color="secondary"
            className={classes.pointsChip}
            label={
              <Typography
                component="span"
                variant="caption"
                className={classes.pointsValue}
              >
                {l(
                  ['r._.goods.text.points', groupPoints],
                  getFormattedValue(groupPoints, {
                    scale: config.classic_points_scale || 0
                  })
                )}
              </Typography>
            }
          />
        </Box>
      ) : null}
    </Card>
  );
};

RawGoodsListItem.defaultProps = {};

RawGoodsListItem.propTypes = {
  data: PropTypes.object.isRequired,

  // `withLang` HOC props
  langInfo: PropTypes.object,
  getLangObject: PropTypes.func,
  getFirstLangString: PropTypes.func,
  getLangStringSet: PropTypes.func,
  getLangString: PropTypes.func.isRequired
};

export const GoodsListItem = withLang(RawGoodsListItem);
